import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import {
  CheckBox,
  PrimaryButton,
  Select,
  TextInput,
} from "@normasteaching/norma-ui";
import { CheckoutPhoneInput } from "../CheckoutPhoneInput.tsx";
import {
  CheckoutPaymentMethod,
  getPaymentMethodDescription,
  getPaymentMethodLabel,
} from "../PaymentMethod.ts";
import {
  getAvailablePaymentMethodsForItem,
  getPaymentStepConfig,
} from "../PaymentSteps/PaymentStepConfigRegistry.ts";
import { useCallback, useEffect, useState } from "react";
import { BillingFormSection } from "../BillingFormSection.tsx";
import { NormasTeachingCheckoutFormData } from "../CheckoutData.ts";
import { validationSchema } from "../CheckoutDataValidationSchema.ts";
import { mapCurrencyToSymbol } from "../CheckoutUtils/mapCurrencyToSymbol.ts";
import { getPaymentPrice } from "../CheckoutUtils/getPaymentPrice.ts";
import { usePrefillValueFromQuery } from "../CheckoutUtils/usePrefillValueFromQuery.ts";
import { CouponSection } from "../CouponSection.tsx";
import { useAllNormasTeachingCoupons } from "../../ProductService/NormasTeachingCoupon/useAllNormasTeachingCoupons.ts";
import {
  CouponType,
  NormasTeachingCouponDTO,
} from "../../ProductService/NormasTeachingCoupon/useNormasTeachingCouponById.ts";
import { useActivePromosForProduct } from "../../ProductService/NormasTeachingPromo/useActivePromoForProduct.ts";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";
import { Check } from "lucide-react";
import { BusinessType } from "../../MoneyTransfer/BusinessType.tsx";

export const NormasTeachingProductCheckoutForm = ({
  product,
  onFormSubmit,
  submitting,
}: {
  product: NormasTeachingProductDTO;
  onFormSubmit: (data: NormasTeachingCheckoutFormData) => void;
  submitting: boolean;
}) => {
  const { email, firstName, lastName, couponCode } = usePrefillValueFromQuery();

  const availablePaymentMethods = getAvailablePaymentMethodsForItem(product);
  const hasPaymentMethods = availablePaymentMethods.length > 0;

  const { coupons, isLoading: isLoadingCoupons } =
    useAllNormasTeachingCoupons();

  const { promos: activePromos } = useActivePromosForProduct(product._id);

  const [couponFromUrlProcessed, setCouponFromUrlProcessed] = useState(false);

  const [appliedCoupon, setAppliedCoupon] = useState<
    NormasTeachingCouponDTO | undefined
  >(undefined);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
  } = useForm<NormasTeachingCheckoutFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      invoiceRequired: false,
      businessType: "",
      businessName: "",
      pec: "",
      sdiCode: "",
      vat: "",
      address: {
        street: "",
        cap: "",
        city: "",
        province: "",
      },
      dialCode: "+39",
      countryCode: "IT",
      number: "",
      taxCode: "",
      customer: {
        email: email ?? "",
        firstName: firstName ?? "",
        lastName: lastName ?? "",
      },
      participants: [],
      paymentMethod: availablePaymentMethods[0],
      numberOfInstallments: undefined,
      bundleParticipants: [],
    },
  });

  //
  const handleCouponApplied = useCallback(
    (coupon: NormasTeachingCouponDTO) => {
      setAppliedCoupon(coupon);
      setValue("coupon", {
        _id: coupon._id,
        couponCode: coupon.couponCode,
        value:
          coupon.value.couponType == CouponType.Fixed
            ? coupon.value.amount
            : (product.priceInCents.amount * coupon.value.amount) / 100,
      });
    },
    [product.priceInCents.amount, setValue],
  );

  const handleCouponRemoved = useCallback(() => {
    setAppliedCoupon(undefined);
    setValue("coupon", undefined);
  }, [setValue]);

  const watchedValues = watch();

  const isCouponApplicableToProduct = useCallback(
    (coupon: NormasTeachingCouponDTO): boolean => {
      if (
        !coupon.applicableProducts ||
        coupon.applicableProducts.length === 0
      ) {
        return true;
      }
      return coupon.applicableProducts.some(
        (ap) =>
          ap.id === product._id &&
          ap.itemType === NormasTeachingItemType.Product,
      );
    },
    [product._id],
  );

  useEffect(() => {
    if (couponFromUrlProcessed) return;

    if (
      couponCode &&
      !isLoadingCoupons &&
      coupons.length > 0 &&
      product.isCouponEnabled
    ) {
      setCouponFromUrlProcessed(true);

      const inputCode = couponCode.trim().toUpperCase();

      const foundCoupon = coupons.find(
        (c) => c.couponCode.toUpperCase() === inputCode,
      );

      if (foundCoupon) {
        const now = new Date();
        const isActive = foundCoupon.isActive;
        const hasUsesLeft =
          foundCoupon.isUnlimitedUses || foundCoupon.maxNumberOfUses > 0;
        const isValidFrom =
          !foundCoupon.validFrom || new Date(foundCoupon.validFrom) <= now;
        const isValidTo =
          !foundCoupon.validTo || new Date(foundCoupon.validTo) >= now;
        const isApplicable = isCouponApplicableToProduct(foundCoupon);

        if (
          isActive &&
          hasUsesLeft &&
          isValidFrom &&
          isValidTo &&
          isApplicable
        ) {
          handleCouponApplied(foundCoupon);
        }
      }
    }
  }, [
    couponCode,
    couponFromUrlProcessed,
    isLoadingCoupons,
    coupons,
    product.isCouponEnabled,
    handleCouponApplied,
    isCouponApplicableToProduct,
  ]);

  useEffect(() => {
    if (activePromos.length > 0) {
      setValue(
        "promos",
        activePromos.map((p) => ({ _id: p._id, bonuses: p.bonuses })),
      );
    } else {
      setValue("promos", undefined);
    }
  }, [activePromos, setValue]);

  useEffect(() => {
    if (
      watchedValues.paymentMethod === CheckoutPaymentMethod.HeyLight &&
      watchedValues.businessType === BusinessType.Company
    ) {
      setValue("businessType", "");
      trigger("businessType");
    }
  }, [
    watchedValues.paymentMethod,
    watchedValues.businessType,
    setValue,
    trigger,
  ]);

  const onSubmit = (data: NormasTeachingCheckoutFormData) => {
    onFormSubmit(data);
  };

  const showInstallmentsSelector =
    watchedValues.paymentMethod === CheckoutPaymentMethod.StripeInstallments;

  const isBusinessInformationRequired =
    watchedValues.paymentMethod === CheckoutPaymentMethod.StripeOneTime ||
    watchedValues.paymentMethod === CheckoutPaymentMethod.StripeInstallments;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {product.productName}
        </h1>
        <div className="mb-4">
          {appliedCoupon ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-lg text-gray-500 line-through">
                {product.priceInCents.amount / 100}{" "}
                {mapCurrencyToSymbol(product.priceInCents.currency)}
              </span>
              <span className="text-2xl font-bold text-grey-600">
                {Math.floor(
                  (product.priceInCents.amount -
                    (watchedValues.coupon?.value || 0)) /
                    100,
                ).toFixed(2)}{" "}
                {mapCurrencyToSymbol(product.priceInCents.currency)}
              </span>
              <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                {appliedCoupon.value.couponType === CouponType.Percentage
                  ? `Sconto ${appliedCoupon.value.amount}%`
                  : `Sconto €${(appliedCoupon.value.amount / 100).toFixed(2)}`}
              </span>
            </div>
          ) : (
            <p className="text-lg text-gray-900">
              {product.priceInCents.amount / 100}{" "}
              {mapCurrencyToSymbol(product.priceInCents.currency)}
            </p>
          )}
        </div>

        {product.productDescription && product.productDescription != "" && (
          <div className="prose prose-sm max-w-none mb-4 [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6">
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
              {product.productDescription}
            </ReactMarkdown>
          </div>
        )}

        {activePromos.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {activePromos.map((promo) => (
              <div
                key={promo._id}
                className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <Check className="text-blue-600 w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium text-blue-800">
                    Promo attiva:{" "}
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {promo.name}
                    </span>
                  </span>
                  {promo.description && (
                    <p className="text-xs text-blue-700 mt-1">
                      {promo.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-gray-600 mb-2">
          Compila i tuoi dati anagrafici per procedere al pagamento.
        </p>
      </div>

      <div className="space-y-6">
        {/* Nome */}
        <div>
          <label className="block text-md font-bold text-gray-900 mb-2">
            Nome
          </label>
          <TextInput
            {...register("customer.firstName")}
            errorMessage={errors.customer?.firstName?.message}
            placeholder="Il tuo nome"
          />
        </div>

        {/* Cognome */}
        <div>
          <label className="block text-md font-bold text-gray-900 mb-2">
            Cognome
          </label>
          <TextInput
            {...register("customer.lastName")}
            errorMessage={errors.customer?.lastName?.message}
            placeholder="Il tuo cognome"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-md font-bold text-gray-900 mb-2">
            Email
          </label>
          <p className={"mb-2 text-gray-600"}>
            Questo indirizzo email sarà utilizzato per accedere al corso e
            ricevere tutte le comunicazioni
          </p>
          <TextInput
            type="email"
            {...register("customer.email")}
            errorMessage={errors.customer?.email?.message}
            placeholder="La tua email"
          />
        </div>

        {watchedValues.paymentMethod === CheckoutPaymentMethod.HeyLight && (
          <div>
            <label className="block text-md font-bold text-gray-900 mb-2">
              Numero di telefono
            </label>
            <CheckoutPhoneInput
              errorMessage={errors.number?.message}
              value={{
                dialCode: watchedValues.dialCode || "",
                countryCode: watchedValues.countryCode || "",
                number: watchedValues.number || "",
              }}
              onNumberChange={() =>
                clearErrors(["number", "dialCode", "countryCode"])
              }
              onChange={(v) => {
                setValue("dialCode", v.dialCode);
                setValue("countryCode", v.countryCode);
                setValue("number", v.number);
              }}
            />
          </div>
        )}

        {/* NormasTeachingCoupon */}
        {product.isCouponEnabled && (
          <CouponSection
            onCouponApplied={handleCouponApplied}
            currentCoupon={appliedCoupon}
            onCouponRemoved={handleCouponRemoved}
            allCoupons={coupons}
            isLoadingCoupons={isLoadingCoupons}
            itemId={product._id}
            itemType={NormasTeachingItemType.Product}
          />
        )}

        {/* Partecipanti extra */}
        {product.peopleCount > 1 && (
          <div className="space-y-6 pt-6">
            <h3 className="text-lg font-bold text-gray-900">
              {product.peopleCount - 1 > 1
                ? "Dati partecipanti aggiuntivi"
                : "Dati partecipante aggiuntivo"}
            </h3>
            <p className={"mb-2 text-gray-600"}>
              Questo è un ordine di gruppo per {product.peopleCount} persone.
              Inserisci i dati degli altri partecipanti qui sotto.
            </p>

            {Array.from({ length: product.peopleCount - 1 }).map((_, index) => (
              <div
                key={index}
                className="border-gray-300 rounded-lg p-4 mb-4 bg-gray-50"
              >
                <h4 className="text-md font-semibold text-gray-700 mb-3">
                  Partecipante {index + 2}
                </h4>

                <div className="grid grid-cols-12 gap-4">
                  {/* Nome */}
                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      Nome
                    </label>
                    <TextInput
                      {...register(`participants.${index}.firstName` as const)}
                      errorMessage={
                        errors.participants?.[index]?.firstName?.message
                      }
                      placeholder="Nome"
                    />
                  </div>

                  {/* Cognome */}
                  <div className="col-span-12 md:col-span-3">
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      Cognome
                    </label>
                    <TextInput
                      {...register(`participants.${index}.lastName` as const)}
                      errorMessage={
                        errors.participants?.[index]?.lastName?.message
                      }
                      placeholder="Cognome"
                    />
                  </div>

                  {/* Email */}
                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-md font-bold text-gray-900 mb-2">
                      Email
                    </label>
                    <TextInput
                      type="email"
                      {...register(`participants.${index}.email` as const)}
                      errorMessage={
                        errors.participants?.[index]?.email?.message
                      }
                      placeholder="Email"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <BillingFormSection
          control={control}
          register={register}
          errors={errors}
          invoiceRequired={watchedValues.invoiceRequired}
          businessType={watchedValues.businessType}
          taxCode={watchedValues.taxCode}
          isBusinessInformationRequired={isBusinessInformationRequired}
          trigger={trigger}
          showCompanyOption={
            watchedValues.paymentMethod !== CheckoutPaymentMethod.HeyLight
          }
          heyLightNote={
            watchedValues.paymentMethod === CheckoutPaymentMethod.HeyLight ? (
              <p className="mb-2">
                Puoi accedere al pagamento tramite finanziaria solo se sei una
                persona fisica o una ditta individuale. Nel caso di acquisti per
                SRL, SPA, SNC o SAS, contattaci a{" "}
                <a
                  href={`mailto:help@normasteaching.com?subject=Richiesta%20dilazione%20pagamento%20società&body=Salve,%0A%0Asono interessato ad acquistare il corso ${product.productName} ma sono una società. Vorrei sapere se è possibile accedere alla dilazione di pagamento con finanziaria`}
                  className="underline text-blue-500"
                >
                  help@normasteaching.com
                </a>
              </p>
            ) : undefined
          }
        />

        {/* Selezione metodo di pagamento */}
        {hasPaymentMethods ? (
          <div className="pt-6 border-t">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Metodo di pagamento
            </h3>
            <Controller
              name="paymentMethod"
              control={control}
              render={({ field }) => (
                <div className="space-y-3">
                  {availablePaymentMethods.map((method) => {
                    const stepConfig = getPaymentStepConfig(method);
                    return (
                      <label
                        key={method}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                          field.value === method
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200"
                        }`}
                      >
                        <input
                          type="radio"
                          value={method}
                          checked={field.value === method}
                          onChange={async () => {
                            field.onChange(method);
                            await trigger([
                              "dialCode",
                              "countryCode",
                              "number",
                            ]);
                          }}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {getPaymentMethodLabel(method)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {getPaymentMethodDescription(method)}
                          </div>
                          <div className="font-sm text-gray-600">
                            {getPaymentPrice(
                              method,
                              product,
                              watchedValues.numberOfInstallments,
                              appliedCoupon,
                            )}
                          </div>
                        </div>
                        {stepConfig?.icons}
                      </label>
                    );
                  })}
                </div>
              )}
            />
            {errors.paymentMethod && (
              <p className="text-red-600 text-sm mt-2">
                {errors.paymentMethod.message}
              </p>
            )}
            {showInstallmentsSelector &&
              product.stripeConfig &&
              product.stripeConfig?.allowedTerms &&
              product.stripeConfig.allowedTerms.length > 0 && (
                <div className="mt-4 bg-blue-50 p-4 rounded-lg">
                  <label className="block text-md font-bold text-gray-900 mb-2">
                    Seleziona il numero di rate
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    Scegli in quante rate vuoi suddividere il pagamento
                  </p>
                  <Controller
                    name="numberOfInstallments"
                    control={control}
                    render={({ field }) => (
                      <Select
                        errorMessage={errors.numberOfInstallments?.message}
                        value={field.value?.toString() ?? ""}
                        onChange={(value) => {
                          field.onChange(
                            value ? parseInt(value, 10) : undefined,
                          );
                        }}
                        options={[
                          {
                            id: "",
                            name: "Seleziona il numero di rate",
                          },
                          ...(product.stripeConfig?.allowedTerms ?? [])
                            .sort((a, b) => a - b)
                            .map((term) => ({
                              id: term.toString(),
                              name: `${term} rate`,
                            })),
                        ]}
                      />
                    )}
                  />
                </div>
              )}
          </div>
        ) : (
          <div className="pt-6 border-t">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-yellow-800 font-medium">
                Nessun metodo di pagamento disponibile per questo prodotto.
              </p>
            </div>
          </div>
        )}

        <div className="pt-6 flex flex-col gap-2">
          <PrimaryButton
            loading={submitting}
            onClick={handleSubmit(onSubmit)}
            label={"Procedi al pagamento"}
            disabled={!hasPaymentMethods}
          />
          <div className={"flex justify-left"}>
            <Controller
              render={({ field }) => {
                return (
                  <CheckBox
                    label={
                      <span>
                        <a
                          className={"text-blue-500 underline"}
                          target={"_blank"}
                          rel={"noopener noreferrer"}
                          href="https://normasteaching.com/legal-info"
                        >
                          Accetto la privacy policy e i termini e condizioni
                        </a>
                      </span>
                    }
                    value={field.value}
                    onChange={field.onChange}
                    errorMessage={errors.privacyPolicyAccepted?.message}
                  />
                );
              }}
              name={"privacyPolicyAccepted"}
              control={control}
            />
          </div>
          <div className={"flex justify-left"}>
            <Controller
              render={({ field }) => {
                return (
                  <CheckBox
                    label={
                      <span>
                        <a>
                          Ai sensi degli artt. 1341 e 1342 c.c., dichiaro di
                          accettare espressamente le clausole delle condizioni
                          generali di vendita: artt. 2.1, 4.3, 6, 10, 13, 16.2,
                          18.5, 20, 21.5, 21.6, 23.2, 26.7, 26.14, 29, 31, 39,
                          41, 47 e 48.
                        </a>
                      </span>
                    }
                    value={field.value}
                    onChange={field.onChange}
                    errorMessage={errors.additionalTerms?.message}
                  />
                );
              }}
              name={"additionalTerms"}
              control={control}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
